import React, { useState, useEffect, useRef } from "react";
import { ReactComponent as Edit3 } from "../../assets/icons/svg/pen.svg";
import { X, Check, Plus, Upload } from "lucide-react";
import { AuthStorage } from "./auth/AuthStorage";
import API_URLS from "../api/urls";

interface DesktopMedicalConditionModalProps {
  isOpen: boolean;
  onClose: () => void;
  condition: string;
  onSave: (condition: string, details: string) => void;
  initialDetails?: string;
}

const DesktopMedicalConditionModal: React.FC<
  DesktopMedicalConditionModalProps
> = ({ isOpen, onClose, condition, onSave, initialDetails = "" }) => {
  const [details, setDetails] = useState(initialDetails);

  useEffect(() => {
    if (isOpen) {
      setDetails(initialDetails);
    }
  }, [isOpen, initialDetails]);

  if (!isOpen) return null;

  const hasDetails = details.trim().length > 0;

  const handleSave = () => {
    if (hasDetails) {
      onSave(condition, details);
      onClose();
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl w-[500px] max-h-[80vh] overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h3 className="text-[18px] font-semibold text-[#202020]">
              {condition} Details
            </h3>
            <button onClick={onClose}>
              <X className="w-5 h-5 text-[#202020]" />
            </button>
          </div>

          <div className="px-6 py-4">
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder={`Please provide details about ${condition}...`}
              rows={6}
              className="w-full px-4 py-3 text-[14px] text-[#202020] font-inter bg-[#F6F7F7] border border-[#E0E0E0] rounded-[12px] focus:outline-none focus:border-[#0DB5B5] resize-none placeholder:text-[#999999]"
            />
          </div>

          <div className="flex gap-3 px-6 py-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="flex-1 py-3 px-4 border border-[#E0E0E0] text-[#646464] rounded-xl font-medium text-[14px] hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!hasDetails}
              className={`flex-1 py-3 px-4 rounded-xl font-medium text-[14px] ${
                hasDetails
                  ? "bg-[#0DB5B5] text-white hover:bg-[#0A9999]"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              Save Details
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

interface UserPersonalPlanData {
  email?: string;
  phone?: string;
  countryCode?: string;
  bestTime?: string;
  profession?: string;
  location?: string;
  heightValue?: string;
  heightUnit?: string;
  heightInches?: string;
  weightValue?: string;
  weightUnit?: string;
  foodPreference?: string;
  medicalConditions?: string[];
  goal?: string;
  conditionDetails?: Record<string, string>;
  user_id?: number;
  user_email?: string;
  user_mobileno?: string;
  user_fname?: string;
  user_lname?: string;
  age?: number;
  city?: string;
  state?: string;
  dob?: string;
  gender?: string;
  height_value?: number;
  height_parameter?: string;
  height_inches?: string;
  weight_value?: number;
  weight_parameter?: string;
  food_preference?: string;
  medical_conditions?: string;
  medical_information?: string;
  target_weight?: number;
  target_weight_parameter?: string;
  nutritionist_id?: string;
  fcm_token?: string;
  profile_url?: string;
  alcohol?: string;
  supplements_intake?: string;
  progress?: string;
  last_status?: string;
  user_apple_id?: string;
  otp?: string;
}

const convertDecimalFeetToFeetInches = (decimalFeet: number) => {
  const feet = Math.floor(decimalFeet);
  const inches = Math.round((decimalFeet - feet) * 12);
  return { feet: feet.toString(), inches: inches.toString() };
};

const convertCmToFeetInches = (cm: number) => {
  const totalInches = cm / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches % 12);
  return { feet: feet.toString(), inches: inches.toString() };
};

interface ExpertConnectionFormProps {
  userId: number | null;
  onBack: () => void;
  onSuccess: () => void;
}

const ExpertConnectionForm: React.FC<ExpertConnectionFormProps> = ({
  userId,
  onBack,
  onSuccess,
}) => {
  const [showMedicalModal, setShowMedicalModal] = useState(false);
  const [selectedCondition, setSelectedCondition] = useState("");
  const [conditionDetails, setConditionDetails] = useState<
    Record<string, string>
  >({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingUserData, setIsLoadingUserData] = useState(false);
  const [userName, setUserName] = useState<string>("");
  const [fileUploadError, setFileUploadError] = useState<string>("");

  const [formData, setFormData] = useState({
    email: "",
    phone: "",
    countryCode: "91",
    bestTime: "",
    profession: "",
    location: "",
    heightValue: "",
    heightUnit: "ft",
    heightInches: "",
    weightValue: "",
    weightUnit: "kg",
    foodPreference: "",
    medicalConditions: [] as string[],
    goal: "",
    medicalReport: null as File | null,
  });

  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  const dropdownRefs = {
    food: useRef<HTMLDivElement>(null),
    time: useRef<HTMLDivElement>(null),
    height: useRef<HTMLDivElement>(null),
    weight: useRef<HTMLDivElement>(null),
    phone: useRef<HTMLDivElement>(null),
  };

  // FIXED: Proper FormData handling with authentication
  const makeAuthenticatedApiCall = async (
    url: string,
    options: RequestInit = {}
  ) => {
    const accessToken = AuthStorage.getAccessToken();

    if (!accessToken) {
      throw new Error("No access token available");
    }

    // Create headers object
    const headers: HeadersInit = {
      Authorization: `Bearer ${accessToken}`,
    };

    // Only add Content-Type for non-FormData requests
    if (!(options.body instanceof FormData)) {
      headers["Content-Type"] = "application/json";
    }

    return fetch(url, {
      ...options,
      headers,
    });
  };

  const fetchUserPersonalPlan = async () => {
    const currentUserId = userId || AuthStorage.getUserId();
    
    if (!currentUserId) {
      console.log("Cannot fetch user data: Missing userId");
      return;
    }

    setIsLoadingUserData(true);

    try {
      console.log(
        "Fetching user personal plan data for user:",
        currentUserId
      );

      const response = await makeAuthenticatedApiCall(
        API_URLS.GET_USER_PERSONAL_PLAN(currentUserId),
        {
          method: "GET",
        }
      );

      if (response.ok) {
        const result = await response.json();
        console.log("User personal plan data received:", result);

        let userData: UserPersonalPlanData = {};

        if (result.user) {
          userData = result.user;
        } else if (result.data) {
          userData = result.data;
        } else {
          userData = result;
        }

        console.log("Extracted user data:", userData);

        const firstName = userData.user_fname || "";
        const lastName = userData.user_lname || "";
        const fullName = `${firstName} ${lastName}`.trim();
        setUserName(fullName);

        setFormData((prevData) => {
          let medicalConditionsArray: string[] = [];
          try {
            if (userData.medical_conditions) {
              let conditionsStr = userData.medical_conditions;
              if (typeof conditionsStr === "string") {
                conditionsStr = conditionsStr.replace(/[\[\]"]/g, "");
                medicalConditionsArray = conditionsStr
                  .split(",")
                  .map((item) => item.trim())
                  .filter((item) => item);
              }
            }
          } catch (error) {
            console.log("Error parsing medical conditions:", error);
            medicalConditionsArray = userData.medical_conditions
              ? [String(userData.medical_conditions)]
              : [];
          }

          let phoneNumber = "";
          let countryCode = "91";
          if (userData.user_mobileno) {
            const fullPhone = userData.user_mobileno.replace(/^\+/, "");
            if (fullPhone.startsWith("91")) {
              countryCode = "91";
              phoneNumber = fullPhone.substring(2);
            } else {
              phoneNumber = fullPhone;
            }
          }

          let heightValue = "";
          let heightUnit = "ft";
          let heightInches = "";

          if (userData.height_value) {
            if (
              userData.height_parameter === "Cm" ||
              userData.height_parameter === "cm"
            ) {
              heightUnit = "cm";
              heightValue = String(userData.height_value);
              heightInches = "";
            } else {
              heightUnit = "ft";
              const { feet, inches } = convertDecimalFeetToFeetInches(
                userData.height_value
              );
              heightValue = feet;
              heightInches = inches;
            }
          }

          let weightValue = "";
          let weightUnit = "kg";

          if (userData.weight_value) {
            weightUnit =
              userData.weight_parameter === "Lbs" ||
              userData.weight_parameter === "lbs"
                ? "lbs"
                : "kg";
            weightValue = String(userData.weight_value);
          }

          const newFormData = {
            ...prevData,
            email: userData.user_email || prevData.email,
            phone: phoneNumber || prevData.phone,
            countryCode: countryCode || prevData.countryCode,
            bestTime: userData.bestTime || prevData.bestTime,
            profession: userData.profession || prevData.profession,
            location: userData.city || prevData.location,
            heightValue: heightValue || prevData.heightValue,
            heightUnit: heightUnit,
            heightInches: heightInches || prevData.heightInches,
            weightValue: weightValue || prevData.weightValue,
            weightUnit: weightUnit,
            foodPreference: userData.food_preference || prevData.foodPreference,
            medicalConditions: medicalConditionsArray,
            goal: userData.goal || prevData.goal,
          };

          console.log("New form data being set:", newFormData);
          return newFormData;
        });

        if (userData.conditionDetails) {
          setConditionDetails(userData.conditionDetails);
          console.log("Condition details set:", userData.conditionDetails);
        }

        console.log("Form auto-filled with user data");
      } else {
        console.error("Failed to fetch user data. Status:", response.status);
        const errorText = await response.text();
        console.error("Error response:", errorText);
      }
    } catch (error) {
      console.error("Error fetching user personal plan:", error);
    } finally {
      setIsLoadingUserData(false);
    }
  };

  useEffect(() => {
    console.log(
      "ExpertConnectionForm - Auth debug info:",
      AuthStorage.getDebugInfo()
    );

    if (AuthStorage.isAuthenticated()) {
      fetchUserPersonalPlan();
    } else {
      console.log("User not authenticated, cannot fetch personal plan data");
    }
  }, [userId]);

  const isFormValid = () => {
    const requiredFields = [
      formData.email,
      formData.phone,
      formData.bestTime,
      formData.profession,
      formData.location,
      formData.heightValue,
      formData.weightValue,
      formData.foodPreference,
      formData.goal,
    ];

    const allFieldsFilled = requiredFields.every(
      (field) => field.trim() !== ""
    );
    const medicalConditionSelected = formData.medicalConditions.length > 0;
    const heightValid =
      formData.heightUnit === "ft" ? formData.heightInches.trim() !== "" : true;
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
    const phoneValid = formData.phone.replace(/\D/g, "").length >= 10;

    return (
      allFieldsFilled &&
      medicalConditionSelected &&
      heightValid &&
      emailValid &&
      phoneValid
    );
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isOutsideAllDropdowns = Object.values(dropdownRefs).every(
        (ref) => ref.current && !ref.current.contains(target)
      );

      if (isOutsideAllDropdowns) {
        setActiveDropdown(null);
      }
    };

    if (activeDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeDropdown]);

  const toggleDropdown = (dropdownName: string) => {
    setActiveDropdown(activeDropdown === dropdownName ? null : dropdownName);
  };

  const closeDropdown = () => {
    setActiveDropdown(null);
  };

  const handleFormSubmit = async () => {
    if (!isFormValid()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const currentUserId = userId || AuthStorage.getUserId();

      console.log("Debug User ID info:");
      console.log("- Props userId:", userId);
      console.log("- AuthStorage getUserId():", AuthStorage.getUserId());
      console.log("- Final currentUserId:", currentUserId);
      console.log("- AuthStorage debug info:", AuthStorage.getDebugInfo());

      if (!currentUserId) {
        alert("User ID not found. Please log in again.");
        setIsSubmitting(false);
        return;
      }

      let heightValue = 0;
      let heightParameter = formData.heightUnit === "ft" ? "ft" : "cm";

      if (formData.heightUnit === "ft") {
        const feet = parseFloat(formData.heightValue) || 0;
        const inches = parseFloat(formData.heightInches) || 0;
        heightValue = parseFloat((feet + inches / 12).toFixed(1));
      } else {
        heightValue = parseFloat(
          parseFloat(formData.heightValue || "0").toFixed(1)
        );
      }

      const weightValue = parseFloat(
        parseFloat(formData.weightValue || "0").toFixed(1)
      );
      const weightParameter = formData.weightUnit;

      let medicalInformation = "";
      const selectedConditions = formData.medicalConditions.filter(
        (condition) => condition !== "None"
      );

      if (selectedConditions.length > 0) {
        medicalInformation = selectedConditions
          .map((condition) => {
            const details = conditionDetails[condition];
            return details ? `${condition}: ${details}` : condition;
          })
          .join(", ");
      }

      const mobileNumber = `+${formData.countryCode}${formData.phone}`;

      const formDataToSend = new FormData();

      // FIXED: Only send fk_user_id
      formDataToSend.append("fk_user_id", currentUserId.toString());

      formDataToSend.append("best_time_to_connect", formData.bestTime);
      formDataToSend.append("what_help_you_are_looking_from_us", formData.goal);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("mobile_number", mobileNumber);
      formDataToSend.append("profession", formData.profession);
      formDataToSend.append("location", formData.location);
      formDataToSend.append("height_parameter", heightParameter);
      formDataToSend.append("height_value", heightValue.toString());
      formDataToSend.append("weight_parameter", weightParameter);
      formDataToSend.append("weight_value", weightValue.toString());
      formDataToSend.append("food_preference", formData.foodPreference);
      formDataToSend.append(
        "medical_conditions",
        JSON.stringify(formData.medicalConditions)
      );
      formDataToSend.append("medical_information", medicalInformation);

      if (formData.medicalReport) {
        formDataToSend.append("medical_report", formData.medicalReport);
      }

      console.log("Expert connection form submitted as FormData");
      console.log("FormData contents:");
      formDataToSend.forEach((value, key) => {
        console.log(
          `${key}: ${value instanceof File ? `File: ${value.name}` : value}`
        );
      });

      try {
        const response = await makeAuthenticatedApiCall(
          API_URLS.UPDATE_PERSONAL_PLAN_REQUEST,
          {
            method: "POST",
            body: formDataToSend,
          }
        );

        console.log("Response status:", response.status);
        console.log("Response headers:", response.headers);

        if (response.ok) {
          const result = await response.json();
          console.log("Expert connection submission response:", result);

          if (result.error) {
            console.error("Backend returned error:", result.error);
            alert(`Submission failed: ${result.error}`);
            return;
          }

          console.log("Submission successful!");
          onSuccess();
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.error("API response error:", errorData);
          throw new Error(`Server responded with status: ${response.status}`);
        }
      } catch (apiError) {
        console.error("API submission failed:", apiError);
        alert("Failed to submit expert connection request. Please try again.");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      alert("An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMedicalConditionToggle = (condition: string) => {
    if (condition === "None") {
      setFormData((prev) => ({
        ...prev,
        medicalConditions: prev.medicalConditions.includes("None")
          ? []
          : ["None"],
      }));
      if (!formData.medicalConditions.includes("None")) {
        setConditionDetails({});
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        medicalConditions: prev.medicalConditions.includes("None")
          ? [condition]
          : prev.medicalConditions.includes(condition)
          ? prev.medicalConditions.filter((c) => c !== condition)
          : [...prev.medicalConditions, condition],
      }));
    }
  };

  const handleConditionEdit = (condition: string) => {
    setSelectedCondition(condition);
    setShowMedicalModal(true);
  };

  const handleConditionDetailsSave = (condition: string, details: string) => {
    setConditionDetails((prev) => ({
      ...prev,
      [condition]: details,
    }));
    setShowMedicalModal(false);
  };

  const handleCloseMedicalModal = () => {
    setShowMedicalModal(false);
    setSelectedCondition("");
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setFileUploadError(""); // Clear previous errors

    if (file) {
      // Check file type
      if (file.type !== "application/pdf") {
        setFileUploadError("Please upload a PDF file only");
        event.target.value = ""; // Reset input
        return;
      }

      // Check file size (5MB = 5 * 1024 * 1024 bytes)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        setFileUploadError(
          "File size exceeds 5MB limit. Please upload a smaller file"
        );
        event.target.value = ""; // Reset input
        return;
      }

      setFormData((prev) => ({ ...prev, medicalReport: file }));
    }
  };
  const removeFile = () => {
    setFormData((prev) => ({ ...prev, medicalReport: null }));
    setFileUploadError(""); // Clear error when removing file
  };

  const timeOptions = [
    "Morning (9am-1pm)",
    "Afternoon (1pm-4pm)",
    "Evening (4pm-9pm)",
  ];
  const foodOptions = [
    "Vegan",
    "Vegetarian",
    "Eggetarian",
    "Pollotarian",
    "Pescatarian",
    "Carnitarian",
    "Non Vegetarian",
  ];
  const countryCodeOptions = [
    "91",
    "1",
    "44",
    "61",
    "81",
    "86",
    "33",
    "49",
    "39",
    "34",
  ];
  const medicalConditions = [
    "None",
    "PCOD",
    "PCOS",
    "Thyroid",
    "Diabetes",
    "Inflammation",
    "Gut health",
    "IBS",
    "Fatty liver",
    "Blood pressure",
    "Heart health",
    "Anemia",
    "Osteoporosis",
    "UTI",
    "Kidney disease",
    "Auto immune",
    "Food intolerance",
    "Mental health",
    "Gout",
    "Others",
  ];

  return (
    <>
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 2px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
          border-radius: 0;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #D1D5DB;
          border-radius: 0;
          border: none;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #9CA3AF;
        }
        
        .custom-scrollbar::-webkit-scrollbar-button:single-button {
          display: none;
        }
        
        .custom-scrollbar::-webkit-scrollbar-button:double-button {
          display: none;
        }
        
        .custom-scrollbar::-webkit-scrollbar-button {
          display: none;
          height: 0;
          width: 0;
        }
        
        .custom-scrollbar::-webkit-scrollbar-corner {
          display: none;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track-piece {
          background: transparent;
        }
        
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #D1D5DB transparent;
        }
      `}</style>

      <div className="flex flex-col gap-4">
        {/* Welcome Container */}
        <div className="border-[0.5px] border-[#6DD0CF] rounded-2xl bg-[#F7FDFD] px-6 py-4 items-center justify-center flex">
          <h2 className="text-[20px] 3xl:text-[24px] font-semibold font-inter text-[#202020] ">
            Welcome back
            <span className="text-[#0DB5B5]"> {userName || "User"}!🎉 </span>
          </h2>
        </div>

        {/* Connect with Experts Form Container */}
        <div className="flex-1 border-[0.5px] border-[#6DD0CF] rounded-2xl bg-white flex flex-col max-h-[450px] xl:max-h-[500px] 2xl:max-h-[500px] 3xl:max-h-[672px] 4xl:max-h-[800px] overflow-hidden">
          {/* Header - Fixed Position */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 flex-shrink-0">
            <h2 className="text-[20px] font-semibold text-[#202020]">
              Connect with Experts
            </h2>
            <button onClick={onBack}>
              <X className="w-4 h-4 text-[#202020]" />
            </button>
          </div>

          {/* Loading indicator - Fixed Position */}
          {isLoadingUserData && (
            <div className="mx-6 mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex-shrink-0">
              <p className="text-blue-700 text-sm">
                Loading your saved information...
              </p>
            </div>
          )}

          {/* Scrollable Content Area with Custom Scrollbar */}
          <div className="flex-1 overflow-y-auto custom-scrollbar">
            <div className="px-6 py-6">
              {/* Row 1: Email (full width) */}
              <div className="mb-4">
                <label className="block text-[16px] 4xl:text-[18px] font-inter font-semibold text-[#828282] mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, email: e.target.value }))
                  }
                  placeholder="Enter your email"
                  className="w-full px-4 py-3 text-[14px] 4xl:text-[16px]  text-[#202020] font-inter font-medium border border-[#E0E0E0] rounded-lg focus:outline-none focus:border-[#0DB5B5]"
                />
              </div>

              {/* Row 2: Phone Number and Best Time to Contact (side by side) */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-[16px] 4xl:text-[18px] font-inter font-semibold text-[#828282] mb-2">
                    Phone number
                  </label>
                  <div className="flex gap-2">
                    <div className="relative" ref={dropdownRefs.phone}>
                      <button
                        onClick={() => toggleDropdown("phone")}
                        className="px-3 py-3 text-[14px] 4xl:text-[16px] text-[#202020] font-inter font-medium border border-[#E0E0E0] rounded-lg bg-white flex items-center gap-1 focus:outline-none focus:border-[#0DB5B5]"
                      >
                        <span>{formData.countryCode}</span>
                        <svg
                          className="w-3 h-3"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                      {activeDropdown === "phone" && (
                        <div className="absolute z-10 left-0 mt-1 bg-white rounded-lg shadow-lg max-h-40 overflow-y-auto border">
                          {countryCodeOptions.map((code) => (
                            <button
                              key={code}
                              onClick={() => {
                                setFormData((prev) => ({
                                  ...prev,
                                  countryCode: code,
                                }));
                                closeDropdown();
                              }}
                              className="w-full px-3 py-2 text-left text-[14px] 4xl:text-[16px] hover:bg-gray-50"
                            >
                              {code}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          phone: e.target.value,
                        }))
                      }
                      placeholder="Enter your phone number"
                      className="w-full px-4 py-3 text-[14px] 4xl:text-[16px] text-[#202020] font-inter font-medium border border-[#E0E0E0] rounded-lg focus:outline-none focus:border-[#0DB5B5]"
                    />
                  </div>
                </div>

                <div className="relative" ref={dropdownRefs.time}>
                  <label className="block text-[16px] 4xl:text-[18px] font-inter font-semibold text-[#828282] mb-2">
                    Best time to contact
                  </label>
                  <button
                    onClick={() => toggleDropdown("time")}
                    className="w-full px-4 py-3 text-[14px] 4xl:text-[16px] text-[#202020] font-inter font-medium border border-[#E0E0E0] bg-white rounded-lg text-left flex justify-between items-center focus:outline-none focus:border-[#0DB5B5]"
                  >
                    <span
                      className={
                        formData.bestTime ? "text-black" : "text-gray-400"
                      }
                    >
                      {formData.bestTime || "Select time"}
                    </span>
                    <svg
                      className="w-3 h-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                  {activeDropdown === "time" && (
                    <div className="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-lg border">
                      {timeOptions.map((time) => (
                        <button
                          key={time}
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              bestTime: time,
                            }));
                            closeDropdown();
                          }}
                          className="w-full p-3 text-[14px] 4xl:text-[16px] text-[#000000] font-inter font-medium text-left hover:bg-gray-50"
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Row 3: Location and Profession (side by side) */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-[16px] 4xl:text-[18px] font-inter font-semibold text-[#828282] mb-2">
                    Location
                  </label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        location: e.target.value,
                      }))
                    }
                    placeholder="Enter your location"
                    className="w-full px-4 py-3 text-[14px] 4xl:text-[16px] text-[#202020] font-inter font-medium border border-[#E0E0E0] rounded-lg focus:outline-none focus:border-[#0DB5B5]"
                  />
                </div>

                <div>
                  <label className="block text-[16px] 4xl:text-[18px] font-inter font-semibold text-[#828282] mb-2">
                    Profession
                  </label>
                  <input
                    type="text"
                    value={formData.profession}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        profession: e.target.value,
                      }))
                    }
                    placeholder="Enter profession"
                    className="w-full px-4 py-3 text-[14px] 4xl:text-[16px] text-[#202020] font-inter font-medium border border-[#E0E0E0] rounded-lg focus:outline-none focus:border-[#0DB5B5]"
                  />
                </div>
              </div>

              {/* Row 4: Height and Weight (side by side) */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-[16px] 4xl:text-[18px] font-inter font-semibold text-[#828282] mb-2">
                    Height
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={formData.heightValue}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          heightValue: e.target.value,
                        }))
                      }
                      placeholder="Feet"
                      className="w-full px-3 py-3 text-[14px] 4xl:text-[16px] text-[#202020] font-inter font-medium border border-[#E0E0E0] rounded-lg focus:outline-none focus:border-[#0DB5B5]"
                    />
                    <div className="relative" ref={dropdownRefs.height}>
                      <button
                        onClick={() => toggleDropdown("height")}
                        className="px-3 py-3 text-[14px] 4xl:text-[16px] text-[#202020] font-inter font-medium border border-[#E0E0E0] rounded-lg bg-white flex items-center gap-1"
                      >
                        <span>{formData.heightUnit}</span>
                        <svg
                          className="w-3 h-3"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                      {activeDropdown === "height" && (
                        <div className="absolute z-10 right-0 mt-1 bg-white rounded-lg shadow-lg border">
                          <button
                            onClick={() => {
                              setFormData((prev) => ({
                                ...prev,
                                heightUnit: "ft",
                              }));
                              closeDropdown();
                            }}
                            className="w-full px-3 py-2 text-left text-[14px] 4xl:text-[16px] hover:bg-gray-50"
                          >
                            ft
                          </button>
                          <button
                            onClick={() => {
                              setFormData((prev) => ({
                                ...prev,
                                heightUnit: "cm",
                              }));
                              closeDropdown();
                            }}
                            className="w-full px-3 py-2 text-left text-[14px] 4xl:text-[16px] hover:bg-gray-50"
                          >
                            cm
                          </button>
                        </div>
                      )}
                    </div>
                    {formData.heightUnit === "ft" && (
                      <>
                        <input
                          type="number"
                          value={formData.heightInches}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              heightInches: e.target.value,
                            }))
                          }
                          placeholder="Inches"
                          className="w-full px-2 py-3 text-[14px] 4xl:text-[16px] text-[#202020] font-inter font-medium border border-[#E0E0E0] rounded-lg focus:outline-none focus:border-[#0DB5B5]"
                        />
                        <span className=" px-4 py-3 text-[14px] 4xl:text-[16px] text-[#202020] font-inter font-medium border border-[#E0E0E0] rounded-lg bg-gray-50 flex items-center">
                          in
                        </span>
                      </>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-[16px] 4xl:text-[18px] font-inter font-semibold text-[#828282] mb-2">
                    Weight
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={formData.weightValue}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          weightValue: e.target.value,
                        }))
                      }
                      placeholder="Enter your weight"
                      className="w-full px-4 py-3 text-[14px] 4xl:text-[16px] text-[#202020] font-inter font-medium border border-[#E0E0E0] rounded-lg focus:outline-none focus:border-[#0DB5B5]"
                    />
                    <div className="relative" ref={dropdownRefs.weight}>
                      <button
                        onClick={() => toggleDropdown("weight")}
                        className="px-3 py-3 text-[14px] 4xl:text-[16px] text-[#202020] font-inter font-medium border border-[#E0E0E0] rounded-lg bg-white flex items-center gap-1"
                      >
                        <span>{formData.weightUnit}</span>
                        <svg
                          className="w-3 h-3"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                      {activeDropdown === "weight" && (
                        <div className="absolute z-10 right-0 mt-1 bg-white rounded-lg shadow-lg border">
                          <button
                            onClick={() => {
                              setFormData((prev) => ({
                                ...prev,
                                weightUnit: "kg",
                              }));
                              closeDropdown();
                            }}
                            className="w-full px-3 py-2 text-left text-[14px] 4xl:text-[16px] hover:bg-gray-50"
                          >
                            kg
                          </button>
                          <button
                            onClick={() => {
                              setFormData((prev) => ({
                                ...prev,
                                weightUnit: "lbs",
                              }));
                              closeDropdown();
                            }}
                            className="w-full px-3 py-2 text-left text-[14px] 4xl:text-[16px] hover:bg-gray-50"
                          >
                            lbs
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 5: Food Preference (full width) */}
              <div className="mb-4 relative" ref={dropdownRefs.food}>
                <label className="block text-[16px] 4xl:text-[18px] font-inter font-semibold text-[#828282] mb-2">
                  Food preference
                </label>
                <button
                  onClick={() => toggleDropdown("food")}
                  className="w-full p-3 border border-[#E0E0E0] rounded-lg bg-white font-inter text-left flex justify-between items-center focus:outline-none focus:border-[#0DB5B5]"
                >
                  <span
                    className={`text-[14px] 4xl:text-[16px] ${
                      formData.foodPreference ? "text-black" : "text-gray-400"
                    }`}
                  >
                    {formData.foodPreference || "Select food preference"}
                  </span>
                  <svg
                    className="w-3 h-3"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                {activeDropdown === "food" && (
                  <div className="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-lg border max-h-48 overflow-y-auto">
                    {foodOptions.map((option) => (
                      <button
                        key={option}
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            foodPreference: option,
                          }));
                          closeDropdown();
                        }}
                        className="w-full p-3 font-inter text-left text-[14px] 4xl:text-[16px] hover:bg-gray-50"
                      >
                        {option}
                        {formData.foodPreference === option && (
                          <Check className="w-4 h-4 float-right mt-0.5" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Row 6: Medical Conditions (with content in two columns) */}
              <div className="mb-4">
                <label className="block text-[16px] 4xl:text-[18px] font-inter font-semibold text-[#828282] mb-2">
                  Medical conditions
                </label>

                <div className=" border border-[#E0E0E0] rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-x-4">
                    {medicalConditions.map((condition, index) => {
                      const isSelected =
                        formData.medicalConditions.includes(condition);
                      const hasDetails = conditionDetails[condition];

                      return (
                        <div key={condition}>
                          <div className="flex items-center justify-between py-2">
                            <label className="flex items-center flex-1 cursor-pointer">
                              <div className="relative">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() =>
                                    handleMedicalConditionToggle(condition)
                                  }
                                  className="sr-only"
                                />
                                <div
                                  className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                                    isSelected
                                      ? "bg-[#0DB5B5] border-[#0DB5B5]"
                                      : "bg-white border-gray-300"
                                  }`}
                                >
                                  {isSelected && (
                                    <Check className="w-3 h-3 text-white" />
                                  )}
                                </div>
                              </div>
                              <div className="ml-3 flex-1">
                                <span className="text-[14px] 4xl:text-[16px] font-medium font-inter text-[#202020]">
                                  {condition}
                                </span>
                                {hasDetails && isSelected && (
                                  <p className="text-[10px] text-[#646464] font-inter mt-1 line-clamp-1">
                                    {hasDetails.length > 20
                                      ? `${hasDetails.substring(0, 20)}...`
                                      : hasDetails}
                                  </p>
                                )}
                              </div>
                            </label>

                            {condition !== "None" && (
                              <button
                                onClick={() => handleConditionEdit(condition)}
                                className="ml-2 p-1 text-[#0DB5B5] hover:text-[#0A9999] flex-shrink-0"
                              >
                                {isSelected ? (
                                  <Edit3 className="w-4 h-4" />
                                ) : (
                                  <Plus className="w-4 h-4" />
                                )}
                              </button>
                            )}
                          </div>
                          {index < medicalConditions.length - 2 && (
                            <div className="border-b border-gray-200"></div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {formData.medicalConditions.length === 0 && (
                    <p className="text-red-500 text-xs mt-2">
                      Please select at least one medical condition
                    </p>
                  )}
                </div>
              </div>

              {/* Row 7: Goal Textarea (full width) */}
              <div className="mb-4">
                <label className="block text-[16px] 4xl:text-[18px] font-inter font-semibold text-[#828282] mb-2">
                  What help you are looking from us?
                </label>
                <textarea
                  value={formData.goal}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, goal: e.target.value }))
                  }
                  placeholder="Write goal"
                  rows={3}
                  className="w-full px-4 py-3 text-[14px] 4xl:text-[16px] text-[#202020] font-inter font-medium bg-[#F6F7F7] rounded-lg focus:outline-none focus:border-[#0DB5B5] resize-none border border-transparent"
                />
              </div>

              {/* Row 8: Medical Report Upload (full width) */}
              <div className="mb-6">
                <label className="block text-[16px] 4xl:text-[18px] font-inter font-semibold text-[#828282] mb-2">
                  Medical report (optional)
                </label>
                {formData.medicalReport ? (
                  <div className="flex items-center justify-between p-3 border border-[#CECECE] rounded-lg bg-[#F9F9F9]">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-red-500 rounded flex items-center justify-center mr-3">
                        <span className="text-white text-xs font-bold">
                          PDF
                        </span>
                      </div>
                      <div>
                        <p className="text-[14px] 4xl:text-[16px] font-semibold font-inter">
                          {formData.medicalReport.name}
                        </p>
                      </div>
                    </div>
                    <button onClick={removeFile} className="text-[#646464]">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <>
                    <label
                      className={`flex flex-col items-center justify-center w-full h-36 border-2 border-dashed rounded-lg cursor-pointer ${
                        fileUploadError
                          ? "border-red-400 bg-red-50"
                          : "border-[#CECECE] bg-gray-50 hover:bg-gray-100"
                      }`}
                    >
                      <div className="flex flex-col items-center justify-center pt-2 pb-3">
                        <Upload
                          className={`w-8 h-8 mb-2 ${
                            fileUploadError ? "text-red-500" : "text-[#0DB5B5]"
                          }`}
                        />
                        <p
                          className={`mb-1 text-[14px] 4xl:text-[16px] ${
                            fileUploadError ? "text-red-500" : "text-[#0DB5B5]"
                          }`}
                        >
                          <span className="font-medium">Click to upload</span>
                        </p>
                        <p className="text-[10px] text-[#8E8E93]">
                          Maximum file 5MB
                        </p>
                      </div>
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                    </label>
                    {fileUploadError && (
                      <p className="text-red-500 text-sm mt-2 flex items-center">
                        <X className="w-4 h-4 mr-1" />
                        {fileUploadError}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Send Button - Directly at bottom of form */}
            <div className="px-6 pb-6 flex justify-end ">
              <button
                onClick={handleFormSubmit}
                disabled={!isFormValid() || isSubmitting}
                className={`px-12 py-3 rounded-lg font-medium text-[14px] transition-colors duration-200 ${
                  isFormValid() && !isSubmitting
                    ? "bg-[#0DB5B5] text-white hover:bg-[#0A9999] cursor-pointer"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                {isSubmitting ? "Sending..." : "Send"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Medical Condition Modal */}
      <DesktopMedicalConditionModal
        isOpen={showMedicalModal}
        onClose={handleCloseMedicalModal}
        condition={selectedCondition}
        onSave={handleConditionDetailsSave}
        initialDetails={conditionDetails[selectedCondition] || ""}
      />
    </>
  );
};

export default ExpertConnectionForm;